import React, { useEffect, useRef, useState } from "react"
import { Link, navigate } from "gatsby"
import styled from "styled-components"

import media from "../../../styles/media"
import { Nav, NavButton, NavLink } from "../Nav"
import { RosterFilterMenu } from "./RosterFilterMenu"
import { RosterLinksMenu } from "./RosterLinksMenu"
import { DesktopLogo } from "./DesktopLogo"
import { DarkSearch, LightSearch, CloseIcon } from "../../icons"
import { useHeaderContext } from "../../context/HeaderContext"

const DesktopWrapper = styled.div`
  display: none;

  ${media.tablet`
    display: block;
    padding: 40px;
  `}
`

const LogoNav = styled.div`
  align-items: flex-start;
  display: flex;
  justify-content: space-between;
  width: 100%;
`

const Logo = styled.div``

const NavWrapper = styled.div`
  width: 50%;
  font-family: StanleyPoster, serif;
  font-size: 21px;
  line-height: 30px;
  margin-top: 0;
`

const DesktopNav = styled(Nav)`
  justify-content: space-between;
  margin-top: -7px;
  margin-left: 22px;

  li {
    &:hover {
      g > g {
        stroke: ${props => props.theme.red};
      }
    }
  }
`

const SearchForm = styled.form`
  display: flex;
  height: ${props => (props.show ? "auto" : 0)};
  overflow: hidden;
  opacity: ${props => (props.show ? 1 : 0)};
  transition: ${props =>
    props.show ? "opacity 80ms ease-in-out 150ms" : "opacity 80ms ease-in-out"};
  margin-bottom: 25px;
  margin-top: 40px;
  position: relative;
  width: 100%;

  input {
    background-color: transparent;
    border: none;
    border-bottom: 1px solid black;
    flex-grow: 1;
    font-family: StanleyRegular, serif;
    font-size: 45px;
    letter-spacing: 0;
    line-height: 52px;
    outline: none;
    padding: 0 6px 10px;
  }

  button {
    margin-top: 0;
    position: absolute;
    bottom: 20px;
    right: 6px;

    &:hover {
      g > g {
        stroke: ${props => props.theme.red};
      }
    }
  }
`

export function DesktopHeader({
  theme = "light",
  isExpanded = false,
  useRosterFiltering = false,
  isScrolled = false,
  publicCategories,
  onRosterButtonClick,
}) {
  const wrapperRef = useRef()
  const [query, setQuery] = useState("")

  const { showSearch, setShowSearch } = useHeaderContext()

  useEffect(() => {
    const handleClick = e => {
      if (wrapperRef.current.contains(e.target) || !isExpanded) {
        return
      }

      onRosterButtonClick()
    }

    window.addEventListener("click", handleClick)

    return () => window.removeEventListener("click", handleClick)
  })

  let areLinksDark = theme === "dark"

  if (isExpanded || showSearch || isScrolled) {
    areLinksDark = true
  }

  let SearchIcon = theme === "light" ? LightSearch : DarkSearch

  if (showSearch || isExpanded || isScrolled) {
    SearchIcon = DarkSearch
  }

  function onSearchClick() {
    setShowSearch(!showSearch)
  }

  return (
    <DesktopWrapper ref={wrapperRef}>
      <LogoNav isScrolled={isScrolled}>
        <Link to="/">
          <Logo theme="dark">
            <DesktopLogo
              className="desktop-logo"
              isScrolled={isScrolled}
              theme={
                !(isExpanded || isScrolled || showSearch) ? theme : areLinksDark
              }
            />
          </Logo>
        </Link>
        <NavWrapper>
          <DesktopNav>
            <li>
              <NavButton
                onMouseOver={
                  !isExpanded || isScrolled ? onRosterButtonClick : undefined
                }
                onClick={onRosterButtonClick}
                areLinksDark={areLinksDark}
                css={`
                  color: ${props =>
                    props.areLinksDark ? props.theme.black : props.theme.white};
                `}
              >
                Roster
              </NavButton>
            </li>
            <li key="about">
              <NavLink dark={areLinksDark} to="/about">
                About
              </NavLink>
            </li>
            <li key="news">
              <NavLink dark={areLinksDark} to="/news">
                News
              </NavLink>
            </li>
            <li key="contact">
              <NavLink dark={areLinksDark} to="/contact">
                Contact
              </NavLink>
            </li>
            <li>
              <NavButton onClick={onSearchClick}>
                {showSearch ? (
                  <CloseIcon alt="Close Search" />
                ) : (
                  <SearchIcon alt="Search" />
                )}
              </NavButton>
            </li>
          </DesktopNav>

          {useRosterFiltering ? (
            <RosterFilterMenu
              show={isExpanded}
              publicCategories={publicCategories}
            />
          ) : (
            <RosterLinksMenu
              show={isExpanded}
              publicCategories={publicCategories}
              dark={areLinksDark}
            />
          )}
        </NavWrapper>
      </LogoNav>

      <SearchForm
        show={showSearch}
        onSubmit={e => {
          e.preventDefault()

          setShowSearch(false)
          navigate(`/search?q=${encodeURIComponent(query)}`)
        }}
      >
        <input
          type="text"
          name="search"
          placeholder="Search"
          aria-label="Search"
          onChange={e => setQuery(e.target.value)}
        />
        <NavButton type="submit">
          <SearchIcon alt="Search" />
        </NavButton>
      </SearchForm>
    </DesktopWrapper>
  )
}
